/**
 * derivedAtoms - Read-only derived atoms that compute the visible graph state.
 *
 * These atoms replace the imperative useEffect chains in the old index.tsx.
 * They automatically recompute when their source atoms change, producing
 * the final set of nodes, edges, and breadcrumbs for rendering.
 *
 * Dependency chain:
 *   Source atoms (baseGraph, focus, exploration, cache)
 *     → derivedAtoms (visible nodes/edges, breadcrumbs)
 *       → React components (read derived atoms, render)
 */

import type { Node, Edge } from '@xyflow/react';
import { atom, type Atom } from 'jotai';
// eslint-disable-next-line @atlassian/tangerine/import/entry-points -- loadable is only available from jotai/utils in jotai v2
import { loadable } from 'jotai/utils';

import {
	layoutMultipleRelationshipGroups,
	type RelationshipGroup,
} from '../layout/ExplorationLayout';
import type { ObjectEdgeData } from '../nodes/ObjectEdge';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import {
	cypherToHyphen,
	extractRelationshipVerb,
	getOtherEndDisplayName,
	extractBestProductFromAtiTypes,
} from '../panels/relationshipDisplayUtils';
import {
	isCacheValid,
	makeNodesCacheKey,
	extractNodesFromResult,
	isSplitRelationship,
	EXPLORATION_DISPLAY_LIMIT,
	type RelationshipInfo,
} from '../services/cypherQueryService';
import {
	twgRelationshipsAsyncAtom,
	NodeNameDisplayMapAtom,
	NodeTypeToAtiTypesMapAtom,
	AtiNodeMetadataMapAtom,
} from '../services/twgSchemaService';
import { resolveNodeMetadataFromAri } from '../utils/ariUtils';

import { BaseNodesAtom, BaseEdgesAtom } from './baseGraphAtoms';
import { QueryCacheAtom } from './cacheAtoms';
import {
	DiscoveredOutgoingAtom,
	DiscoveredIncomingAtom,
	LoadingRelKeysAtom,
} from './explorationAtoms';
import { FocusedNodeAtom, SelectedRelationshipsAtom } from './focusAtoms';

// ============================================================================
// Display-ready relationship atoms
// ============================================================================

/**
 * A RelationshipInfo enriched with pre-computed display fields.
 * Display layers should consume these atoms instead of the raw
 * DiscoveredOutgoing/IncomingAtoms so they never need to inspect
 * compound keys or hardcode relationship-name checks.
 */
export type DisplayRelationshipInfo = RelationshipInfo & {
	/** The relationship type string for display / edge labels (hyphenated) */
	displayType: string;
	/** Human-readable verb, e.g. "linked to", "viewed", "assigned" */
	displayVerb: string;
	/** Human-readable name for the other end, e.g. "Confluence Blogpost" */
	targetDisplayName: string;
	/** Product key for grouping (e.g. "confluence", "jira"). Derived from ATI types. */
	productKey?: string;
};

/**
 * Enriches raw RelationshipInfo entries with display metadata.
 * For split entries (sourceRelType != null) we derive display fields
 * from the metadata. For regular entries we fall back to the schema-
 * based resolution functions.
 */
/**
 * Returns the product display name (e.g. "GitHub") inferred from the
 * hydrated nodes on a `RelationshipInfo`, or null when none of the nodes
 * carry a `thirdPartyId`. Used to rebrand "External X" → "<Product> X"
 * across the panel and edge layers without mutating the underlying
 * typename.
 */
function inferProductFromRelNodes(rel: RelationshipInfo): string | null {
	if (!rel.nodes || rel.nodes.length === 0) {
		return null;
	}
	for (const node of rel.nodes) {
		const product = node.properties?.productDisplayName;
		if (typeof product === 'string' && product.length > 0) {
			return product;
		}
	}
	return null;
}

/**
 * Returns the resolved typename of the first hydrated node attached to
 * the relationship. Used as the source-of-truth display typename when
 * the registry's `targetTypeName` would otherwise expose a bridge node
 * (e.g. `ThirdPartyRemoteLink` instead of the projected `ExternalRemoteLink`).
 */
function inferTypenameFromRelNodes(rel: RelationshipInfo): string | null {
	if (!rel.nodes || rel.nodes.length === 0) {
		return null;
	}
	for (const node of rel.nodes) {
		if (typeof node.typename === 'string' && node.typename.length > 0) {
			return node.typename;
		}
	}
	return null;
}

/**
 * Replaces the leading `"External"` token in a display string with the
 * supplied product name. Returns the original string unchanged when the
 * `External` prefix isn't present so callers can use it unconditionally.
 */
function rebrandExternalDisplay(display: string, productName: string | null): string {
	if (!productName) {
		return display;
	}
	if (display === 'External') {
		return productName;
	}
	if (display.startsWith('External ')) {
		return `${productName} ${display.slice('External '.length)}`;
	}
	return display;
}

/**
 * Resolves the user-facing target display name for a relationship,
 * preferring the actual hydrated node's typename over the registry's
 * `targetTypeName`. This is what hides bridges like `ThirdPartyRemoteLink`
 * from the panel: when the multi-hop returns `ExternalRemoteLink` nodes,
 * we surface "External Remote Link" (and then rebrand it to "<Product>
 * Remote Link" via `productDisplayName`).
 */
function resolveTargetDisplayName(
	rel: RelationshipInfo,
	direction: 'incoming' | 'outgoing',
	allRelationships: Record<string, any>,
	typenameDisplayMap: Record<string, string>,
	productName: string | null,
): string {
	const hydratedTypename = inferTypenameFromRelNodes(rel);
	let base: string;
	if (hydratedTypename) {
		base =
			typenameDisplayMap[hydratedTypename] || hydratedTypename.replace(/([a-z])([A-Z])/g, '$1 $2');
	} else {
		base = getOtherEndDisplayName(rel.type, direction, allRelationships, typenameDisplayMap);
	}
	return rebrandExternalDisplay(base, productName);
}

function enrichRelationships(
	relationships: RelationshipInfo[],
	direction: 'incoming' | 'outgoing',
	allRelationships: Record<string, any>,
	typenameDisplayMap: Record<string, string>,
	typenameToAtiTypes: Record<string, string[]>,
): DisplayRelationshipInfo[] {
	return relationships.map((rel) => {
		const productName = inferProductFromRelNodes(rel);

		if (isSplitRelationship(rel)) {
			const nodeType = rel.targetNodeType!;
			const atiTypes = typenameToAtiTypes[nodeType];
			const productKey =
				atiTypes && atiTypes.length > 0
					? (extractBestProductFromAtiTypes(atiTypes) ?? undefined)
					: undefined;
			const baseTargetDisplay =
				typenameDisplayMap[nodeType] || nodeType.replace(/([a-z])([A-Z])/g, '$1 $2');
			return {
				...rel,
				displayType: cypherToHyphen(rel.sourceRelType!),
				displayVerb: 'linked to',
				targetDisplayName: rebrandExternalDisplay(baseTargetDisplay, productName),
				productKey,
			};
		}
		return {
			...rel,
			displayType: rel.type,
			displayVerb: extractRelationshipVerb(rel.type, allRelationships),
			targetDisplayName: resolveTargetDisplayName(
				rel,
				direction,
				allRelationships,
				typenameDisplayMap,
				productName,
			),
		};
	});
}

const loadableTwgRelationshipsForDisplay = loadable(twgRelationshipsAsyncAtom);

/**
 * Display-ready outgoing relationships.
 * Consumers read pre-computed displayVerb / targetDisplayName / displayType
 * instead of calling extractRelationshipVerb / getOtherEndDisplayName directly.
 */
export const DisplayOutgoingAtom: Atom<DisplayRelationshipInfo[]> = atom<DisplayRelationshipInfo[]>(
	(get) => {
		const raw = get(DiscoveredOutgoingAtom);
		const loadedRels = get(loadableTwgRelationshipsForDisplay);
		const allRelationships: Record<string, any> =
			loadedRels.state === 'hasData' && loadedRels.data
				? (loadedRels.data as Record<string, any>)
				: {};
		const typenameDisplayMap = get(NodeNameDisplayMapAtom);
		const typenameToAtiTypes = get(NodeTypeToAtiTypesMapAtom);
		return enrichRelationships(
			raw,
			'outgoing',
			allRelationships,
			typenameDisplayMap,
			typenameToAtiTypes,
		);
	},
);

/**
 * Display-ready incoming relationships.
 */
export const DisplayIncomingAtom: Atom<DisplayRelationshipInfo[]> = atom<DisplayRelationshipInfo[]>(
	(get) => {
		const raw = get(DiscoveredIncomingAtom);
		const loadedRels = get(loadableTwgRelationshipsForDisplay);
		const allRelationships: Record<string, any> =
			loadedRels.state === 'hasData' && loadedRels.data
				? (loadedRels.data as Record<string, any>)
				: {};
		const typenameDisplayMap = get(NodeNameDisplayMapAtom);
		const typenameToAtiTypes = get(NodeTypeToAtiTypesMapAtom);
		return enrichRelationships(
			raw,
			'incoming',
			allRelationships,
			typenameDisplayMap,
			typenameToAtiTypes,
		);
	},
);

// ============================================================================
// Constants
// ============================================================================

/**
 * Prefix used to create unique ReactFlow IDs for explored nodes that duplicate
 * base graph nodes. When an explored relationship reveals a node that already
 * exists in the base graph, we prefix its ReactFlow ID with this string so both
 * the dimmed base node and the active explored copy can coexist.
 *
 * Example: base node "ari:cloud:jira:...:issue/123" gets an explored duplicate
 * with ID "explore-ari:cloud:jira:...:issue/123"
 */
export const EXPLORED_NODE_ID_PREFIX = 'explore-';

// ============================================================================
// Breadcrumb derived atom
// ============================================================================

export interface BreadcrumbItem {
	label: string;
	ari: string;
	isCurrent: boolean;
	/** Index into NavigationPathAtom, or -1 for focused node, or -2 for "Results" */
	navigationIndex: number;
}

// ============================================================================
// Shared exploration group building (consumed by both nodes and edges atoms)
// ============================================================================

/**
 * Derives the exploration RelationshipGroup[] from selected relationships,
 * loading state, and cache. This is shared between VisibleNodesAtom and
 * VisibleEdgesAtom to avoid duplicating the group-building logic.
 *
 * Returns { groups, layoutResult } where layoutResult is the dagre-laid-out
 * version of the groups.
 */
const ExplorationLayoutResultAtom = atom((get) => {
	const focusedNode = get(FocusedNodeAtom);
	const emptyResult = { nodes: [] as Node<ObjectNodeData>[], edges: [] as Edge<ObjectEdgeData>[] };

	if (!focusedNode) {
		return emptyResult;
	}

	const selectedRelationships = get(SelectedRelationshipsAtom);
	const baseNodes = get(BaseNodesAtom);
	const loadingKeys = get(LoadingRelKeysAtom);
	const queryCache = get(QueryCacheAtom);
	const atiNodeMap = get(AtiNodeMetadataMapAtom);

	const loadedRels = get(loadableTwgRelationshipsForDisplay);
	const allRelationships: Record<string, any> =
		loadedRels.state === 'hasData' && loadedRels.data
			? (loadedRels.data as Record<string, any>)
			: {};

	const groups: RelationshipGroup[] = [];

	for (let relIdx = 0; relIdx < selectedRelationships.length; relIdx++) {
		const rel = selectedRelationships[relIdx];
		const isLoading = loadingKeys.has(rel.key);
		// Use pre-computed display metadata stored on the SelectedRelationship
		// (persists even when the active node shifts to a different child).
		const displayLabel = rel.displayVerb ?? extractRelationshipVerb(rel.relType, allRelationships);
		const edgeRelType = rel.displayType ?? rel.relType;

		// Find the source node — could be the focused base node or an explored child
		let sourceNode = baseNodes.find((n) => n.id === rel.sourceNodeAri);
		if (!sourceNode) {
			// Source is an explored child — look in the groups we've already built
			for (const group of groups) {
				const found = group.exploredNodes.find((n) => n.id === rel.sourceNodeAri);
				if (found) {
					sourceNode = found;
					break;
				}
			}
		}
		if (!sourceNode) {
			// Create a minimal source node at the focused node's position
			const focusBaseNode = baseNodes.find((n) => n.id === focusedNode.id);
			if (!focusBaseNode) {
				continue;
			}
			sourceNode = {
				id: rel.sourceNodeAri,
				type: 'objectNode',
				position: focusBaseNode.position,
				data: {
					id: rel.sourceNodeAri,
					typename: 'Unknown',
					displayName: rel.sourceNodeName,
					url: null,
					properties: {},
				},
			};
		}

		if (isLoading) {
			groups.push({
				relationshipType: edgeRelType,
				displayLabel,
				direction: rel.direction,
				sourceNode,
				exploredNodes: [],
				isLoading: true,
				colorIndex: relIdx,
			});
		} else {
			const cacheKey = makeNodesCacheKey(rel.sourceNodeAri, rel.direction, rel.relType);
			const cached = queryCache.get(cacheKey);

			if (!isCacheValid(cached)) {
				continue;
			}

			const rawNodes = extractNodesFromResult(cached.result, atiNodeMap);

			// Sort by lastUpdated descending so that the most recently updated
			// nodes appear first (top-to-bottom in the layout). Nodes without
			// lastUpdated are placed at the end.
			const sortedRawNodes = [...rawNodes].sort((a, b) => {
				const aDate = a.properties?.lastUpdated;
				const bDate = b.properties?.lastUpdated;
				if (!aDate && !bDate) {
					return 0;
				}
				if (!aDate) {
					return 1;
				}
				if (!bDate) {
					return -1;
				}
				return String(bDate).localeCompare(String(aDate));
			});

			// Display only the top EXPLORATION_DISPLAY_LIMIT nodes on the graph.
			// The full set is still fetched and cached for counting purposes.
			const displayedRawNodes = sortedRawNodes.slice(0, EXPLORATION_DISPLAY_LIMIT);

			const exploredNodes: Node<ObjectNodeData>[] = displayedRawNodes.map((nodeData) => {
				const baseTypenameDisplay =
					(nodeData.id.startsWith('ari:')
						? resolveNodeMetadataFromAri(nodeData.id, atiNodeMap)?.displayName
						: undefined) ?? nodeData.typename;
				const productDisplayName =
					typeof nodeData.properties?.productDisplayName === 'string'
						? (nodeData.properties.productDisplayName as string)
						: null;
				return {
					id: nodeData.id,
					type: 'objectNode' as const,
					position: { x: 0, y: 0 },
					data: {
						...nodeData,
						typenameDisplay: rebrandExternalDisplay(baseTypenameDisplay, productDisplayName),
					},
				};
			});

			if (exploredNodes.length > 0) {
				groups.push({
					relationshipType: edgeRelType,
					displayLabel,
					direction: rel.direction,
					sourceNode,
					exploredNodes,
					colorIndex: relIdx,
				});
			}
		}
	}

	return layoutMultipleRelationshipGroups(groups, focusedNode.id);
});

// ============================================================================
// Visible graph derived atoms
// ============================================================================

/**
 * Derives the set of node ARIs that are currently waiting on related-data
 * fetches. A node is "loading data" iff there exists at least one selected
 * relationship sourced from that node whose key appears in
 * `LoadingRelKeysAtom`.
 *
 * Consumed by `VisibleNodesAtom` to set `data.isLoadingData` on the
 * matching ObjectNode(s) so they can display a rotating gradient border
 * loading affordance. The set clears automatically when the fetch
 * completes (key removed from `LoadingRelKeysAtom`) or when the user
 * deselects the focused node (`focusActions.ts` clears the loading set).
 */
export const LoadingNodeIdsAtom: Atom<Set<string>> = atom<Set<string>>((get) => {
	const loadingKeys = get(LoadingRelKeysAtom);
	if (loadingKeys.size === 0) {
		return new Set<string>();
	}
	const selectedRelationships = get(SelectedRelationshipsAtom);
	const result = new Set<string>();
	for (const rel of selectedRelationships) {
		if (loadingKeys.has(rel.key)) {
			result.add(rel.sourceNodeAri);
		}
	}
	return result;
});

/**
 * Derives the complete set of visible nodes from base graph + focus state + exploration results.
 *
 * When NOT in focus mode: returns all base nodes as-is.
 * When IN focus mode:
 *   - The focused base node is fully visible (opacity: 1)
 *   - Other base nodes are dimmed (opacity: 0.12, pointerEvents: none)
 *   - Explored nodes from selected relationships are positioned via dagre layout
 *     and fully visible. If an explored node shares an ID with a base node, it
 *     gets an "explore-" prefix so both the dimmed original and the active
 *     duplicate coexist in ReactFlow.
 *
 * Additionally, any object node whose ARI is in `LoadingNodeIdsAtom` has
 * `data.isLoadingData = true` set so the renderer can display a rotating
 * gradient border while related-data fetches are in flight. This applies
 * to both the original base node and any "explore-" prefixed duplicate
 * for the same underlying ARI, so the indicator follows the node wherever
 * it appears.
 */
export const VisibleNodesAtom: Atom<Node<ObjectNodeData>[]> = atom<Node<ObjectNodeData>[]>(
	(get) => {
		const baseNodes = get(BaseNodesAtom);
		const focusedNode = get(FocusedNodeAtom);
		const loadingNodeIds = get(LoadingNodeIdsAtom);

		const withLoadingFlag = (node: Node<ObjectNodeData>, ariForLoading: string) => {
			const isLoadingData = loadingNodeIds.has(ariForLoading);
			// Only spread when the value actually needs to change to keep
			// referential equality stable for unaffected nodes.
			if (isLoadingData === Boolean(node.data?.isLoadingData)) {
				return node;
			}
			return {
				...node,
				data: {
					...node.data,
					isLoadingData,
				},
			};
		};

		// Not in focus mode → return base nodes as-is (with loading flag applied
		// to any object node currently waiting on related data)
		if (!focusedNode) {
			return baseNodes.map((node) =>
				node.type === 'objectNode' ? withLoadingFlag(node, node.id) : node,
			);
		}

		const layoutResult = get(ExplorationLayoutResultAtom);

		// In focus mode: fade all non-focused base nodes (including groups and header).
		// Group and header nodes get faded; only the focused object node stays fully visible.
		const updatedBaseNodes = baseNodes.map((node) => {
			const isFocusedObjectNode = node.type === 'objectNode' && node.id === focusedNode.id;

			const styled: Node<ObjectNodeData> = {
				...node,
				style: {
					...node.style,
					opacity: isFocusedObjectNode ? 1 : 0.12,
					pointerEvents: isFocusedObjectNode ? ('auto' as const) : ('none' as const),
					transition: 'opacity 0.3s ease-in-out',
				},
			};

			return node.type === 'objectNode' ? withLoadingFlag(styled, node.id) : styled;
		});

		// Build a set of base node IDs so we can assign unique IDs to explored nodes
		// that duplicate a base node. ReactFlow requires unique node IDs — if two nodes
		// share the same ID, only the last one renders. We prefix duplicates with
		// "explore-" so both the dimmed base node and the active explored copy coexist.
		const baseNodeIds = new Set(baseNodes.map((n) => n.id));

		// Explored nodes: visible, with duplicates of base nodes given unique IDs
		const exploredNodes = layoutResult.nodes.map((node) => {
			// Use the underlying ARI (not the "explore-" prefixed id) when looking
			// up loading state, since LoadingNodeIdsAtom is keyed on canonical ARIs.
			const underlyingAri = node.id;
			const styled: Node<ObjectNodeData> = {
				...node,
				// If this explored node has the same ID as a base node, give it a
				// unique "explore-" prefixed ID so both can coexist in ReactFlow
				id: baseNodeIds.has(node.id) ? `${EXPLORED_NODE_ID_PREFIX}${node.id}` : node.id,
				style: {
					...node.style,
					opacity: 1,
					pointerEvents: 'auto' as const,
					transition: 'opacity 0.2s ease-in-out',
				},
			};
			return withLoadingFlag(styled, underlyingAri);
		});

		return [...updatedBaseNodes, ...exploredNodes];
	},
);

/**
 * Derives the complete set of visible edges from base graph + exploration results.
 *
 * When NOT in focus mode: returns all base edges as-is.
 * When IN focus mode:
 *   - Base edges are hidden (opacity: 0)
 *   - Exploration edges from selected relationships are visible with colors
 *   - Edge source/target IDs are remapped to "explore-" prefixed IDs when
 *     the node is a base node duplicate (GRAPHYTE-2171)
 */
export const VisibleEdgesAtom: Atom<Edge<ObjectEdgeData>[]> = atom<Edge<ObjectEdgeData>[]>(
	(get) => {
		const baseEdges = get(BaseEdgesAtom);
		const focusedNode = get(FocusedNodeAtom);

		// Not in focus mode → return base edges as-is
		if (!focusedNode) {
			return baseEdges;
		}

		// In focus mode: hide all base edges
		const updatedBaseEdges = baseEdges.map((edge) => ({
			...edge,
			style: {
				...edge.style,
				opacity: 0,
				transition: 'opacity 0.2s ease-in-out',
			},
			labelStyle: { opacity: 0, transition: 'opacity 0.2s ease-in-out' },
		}));

		// Get exploration edges from shared atom
		const layoutResult = get(ExplorationLayoutResultAtom);

		// Remap edge source/target to the "explore-" prefixed ID when the node
		// is a base node that was duplicated. The focused node keeps its original
		// ID (it's the only base node that isn't dimmed). All other base nodes
		// that appear as explored get an "explore-" prefix on their duplicate.
		const baseNodes = get(BaseNodesAtom);
		const baseNodeIds = new Set(baseNodes.map((n) => n.id));

		const remappedEdges = layoutResult.edges.map((edge) => {
			const remapId = (id: string) => {
				// Don't remap the focused node — it keeps its original ID
				if (id === focusedNode.id) {
					return id;
				}
				// Remap base-node IDs to their explored duplicate
				if (baseNodeIds.has(id)) {
					return `${EXPLORED_NODE_ID_PREFIX}${id}`;
				}
				return id;
			};

			return {
				...edge,
				source: remapId(edge.source),
				target: remapId(edge.target),
			};
		});

		return [...updatedBaseEdges, ...remappedEdges];
	},
);

// ============================================================================
// Visible graph adjacency (for BFS navigation path computation)
// ============================================================================

/**
 * Strips the "explore-" prefix from a node ID if present.
 * Explored duplicates of base nodes use "explore-{ari}" as their ReactFlow ID,
 * but the adjacency map and BFS navigation use original ARIs.
 */
function stripExplorePrefix(id: string): string {
	return id.startsWith(EXPLORED_NODE_ID_PREFIX) ? id.slice(EXPLORED_NODE_ID_PREFIX.length) : id;
}

/**
 * Derives a bidirectional adjacency map from all visible edges.
 *
 * This captures the actual rendered graph topology — every edge that the
 * user can see on screen is represented. Used by `DrillIntoChildAtom` to
 * compute the shortest breadcrumb path between the focused node and any
 * visible node via BFS.
 *
 * Edge source/target IDs are normalized back to original ARIs (stripping
 * any "explore-" prefix) so that BFS works with the canonical node IDs
 * used throughout the navigation system (GRAPHYTE-2171).
 *
 * Shape: `Map<nodeAri, Set<neighbourAri>>` (bidirectional)
 */
export const VisibleGraphAdjacencyAtom: Atom<Map<string, Set<string>>> = atom<
	Map<string, Set<string>>
>((get) => {
	const edges = get(VisibleEdgesAtom);
	const adjacency = new Map<string, Set<string>>();

	const addEdge = (a: string, b: string) => {
		let neighbours = adjacency.get(a);
		if (!neighbours) {
			neighbours = new Set<string>();
			adjacency.set(a, neighbours);
		}
		neighbours.add(b);
	};

	for (const edge of edges) {
		// Skip hidden base edges (opacity 0) — they are not part of the
		// active exploration topology
		if (edge.style && typeof edge.style.opacity === 'number' && edge.style.opacity === 0) {
			continue;
		}
		// Normalize "explore-" prefixed IDs back to original ARIs so BFS
		// works with the canonical node IDs used by navigation actions.
		const source = stripExplorePrefix(edge.source);
		const target = stripExplorePrefix(edge.target);
		addEdge(source, target);
		addEdge(target, source);
	}

	return adjacency;
});
